import { Flex } from '@chakra-ui/layout';
import {
  Alert,
  AlertIcon,
  AlertTitle,
  IconButton,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  UseDisclosureProps,
  VStack,
} from '@chakra-ui/react';
import { Icon } from '@common/components/Icon';
import { useBankAccountsModel } from '@common/models/BankAccountsModel';
import React, { useState } from 'react';
import PlaidLink from 'src/apps/invest/common/components/PlaidLink';
import { PlaidLinkOnSuccessMetadata } from 'react-plaid-link';
import { TextBlock } from 'src/apps/invest/common/components/TextBlock';
import { useSurveyBannerModel } from '../models/SurveyBannerModel';

type AddBankAccountProps = {
  modalProps: UseDisclosureProps;
};

export const AddBankAccount: React.FC<AddBankAccountProps> = ({ modalProps }) => {
  const [error, setError] = useState<string | null>(null);

  const model = useSurveyBannerModel();
  const bankAccountsModel = useBankAccountsModel();

  const onStartPlaidLink = () => {
    setError(null);
  }
  
  const onFinishPlaidLink = async (publicToken: string, metadata: PlaidLinkOnSuccessMetadata) => {
    try {
      const res = await model.addBankAccount(publicToken, metadata);
      if (res.success) {
        await bankAccountsModel.fetchBankAccounts();
        model.nextStep();
      } else if (res.error) {
        setError(res.error);
      }
    } catch (err: any) {
      setError('Unable to add bank account');
    }
  };

  return (
    <ModalContent>
      <ModalHeader
        display={'flex'}
        alignItems={'center'}
        justifyContent={'space-between'}
        borderBottomWidth={'1px'}
        borderBottomColor={'gray.300'}>
        <IconButton
          color="black"
          variant={'ghost'}
          aria-label="Back"
          icon={<Icon icon="chevron-left" />}
          onClick={() => model.prevStep()}
        />
        Bank Account
        <IconButton
          color="black"
          variant={'ghost'}
          aria-label="Back"
          icon={<Icon icon="times" />}
          onClick={modalProps.onClose}
        />
      </ModalHeader>
      <ModalBody>
        <Flex flex={1} justifyContent={'space-between'} flexDirection={'column'} display={'flex'}>
          <VStack alignItems="left" py={'20px'} gap="10px" mb="50px">
            <TextBlock textAlign="start" weight="bold" fontSize={'20px'} lineHeight={'20px'}>
              {'Add a bank account'}
            </TextBlock>
            <TextBlock fontSize={'14px'}>
              We use Plaid as a partner to help connect your bank account to Concreit. Please click the button below to
              add an account
            </TextBlock>
            {error && (
              <Alert status='error'>
                <AlertIcon />
                <AlertTitle>{error}</AlertTitle>
              </Alert>
            )}
          </VStack>
        </Flex>
      </ModalBody>
      <ModalFooter>
        <PlaidLink
          onStart={onStartPlaidLink}
          onSuccess={onFinishPlaidLink}
          buttonProps={{
            children: 'Add Account',
            colorScheme: "blue",
            w: '100%',
          }}
        />
      </ModalFooter>
    </ModalContent>
  );
};
